/**
 * SupplierDashboardPage — /dashboard/supplier
 *
 * Live dashboard: profile + strength bar, four counter tiles wired to
 * the real chat/lead/booking APIs, chat-quota pill, and a first-lead
 * upgrade nudge that fires once the free supplier has at least one
 * lead — driving Studio Pass conversion earlier in the funnel.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PencilSimple, Envelope, ChatsCircle, CheckCircle, CalendarBlank,
  Eye, Lock, Lightning, House, SignOut,
} from '@phosphor-icons/react';
import SupplierAttributionCard from '../components/SupplierAttributionCard';
import SupplierExternalBookingModal from '../components/SupplierExternalBookingModal';
import { setPreferredView } from '../utils/postAuthRoute';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_EMOJI = {
  dj: '🎧', photographer: '📷', videographer: '🎥',
  photobooth: '📸', caterer: '🍽️',
  florist: '🌼', venue: '🏛️', cake_maker: '🎂',
  transport: '🚗', decorator: '🎨',
  wedding_planner: '✨', hair_makeup: '💅',
};

const SupplierDashboardPage = () => {
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [strength, setStrength] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const [leads, setLeads] = useState([]);
  const [notifications, setNotifications] = useState({ unread_messages: 0, new_leads: 0 });
  const [fayreUnread, setFayreUnread] = useState({ total_new: 0, total_joined: 0 });
  const [hasEvents, setHasEvents] = useState(false);
  // External-booking modal — opens from a CTA below the section
  // cards. Hook lives here at the top of the component so the
  // Rules of Hooks are honoured across the loading/auth guards.
  const [externalModalOpen, setExternalModalOpen] = useState(false);
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login?logged_out=true');
  };

  const refresh = useCallback(async () => {
    try {
      const [me, q, lds, nts, fu] = await Promise.all([
        axios.get(`${API}/suppliers/me`),
        axios.get(`${API}/suppliers/me/chat-quota`),
        axios.get(`${API}/suppliers/me/leads`),
        axios.get(`${API}/me/notifications`),
        axios.get(`${API}/suppliers/me/fayres/unread-summary`).catch(() => ({ data: { total_new: 0, total_joined: 0 } })),
      ]);
      setSupplier(me.data.supplier);
      setStrength(me.data.strength || 0);
      setQuota(q.data);
      setLeads(lds.data.leads || []);
      setNotifications(nts.data);
      setFayreUnread(fu.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setSupplier(null);
      }
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    const t = setInterval(refresh, 20000);
    // Detect dual-role for the "Switch to host view" toggle
    axios.get(`${API}/events`)
      .then(r => {
        const list = Array.isArray(r?.data) ? r.data : (r?.data?.events || []);
        setHasEvents(list.length > 0);
      })
      .catch(() => {});
    return () => clearInterval(t);
  }, [refresh]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center">
        <p className="text-white/40 text-sm">Loading dashboard…</p>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center p-8" data-testid="dashboard-auth-required">
        <div className="text-center">
          <p className="font-bold mb-2">Please log in to view your supplier dashboard</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-2 px-4 py-2 rounded-lg text-sm font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            Log in
          </button>
        </div>
      </div>
    );
  }

  const incomplete = !supplier.is_live;
  const isPro = quota?.is_pro;
  const bookedCount = leads.filter((l) => l.status === 'booked').length;
  const chatCount = leads.length; // 1 chat ≈ 1 lead
  const newLeadCount = leads.filter((l) => l.status === 'new').length;
  const totalUnread = notifications.unread_messages || 0;
  const showFirstLeadNudge = !isPro && leads.length > 0;
  // External-booking modal — entry point also rendered just below
  // the Bookings tile so suppliers can capture an off-platform
  // enquiry from the dashboard without navigating away.

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="supplier-dashboard">
      <div className="max-w-4xl mx-auto px-5 py-8">
        {/* Top-row controls — Switch view (when dual-role) + Sign out.
           Sign out lives here on every supplier session so users can
           always exit cleanly without hunting through menus. */}
        <div className="flex justify-end items-center gap-2 mb-3">
          {hasEvents && (
            <button
              onClick={() => {
                setPreferredView('host');
                navigate('/dashboard');
              }}
              className="px-3 py-1.5 rounded-full text-[11px] font-bold text-white/70 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:text-white transition-colors flex items-center gap-1.5"
              data-testid="switch-to-host"
            >
              <House size={12} weight="bold" /> Switch to host view
            </button>
          )}
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 rounded-full text-[11px] font-bold text-white/55 border border-white/12 hover:text-red-300 hover:border-red-300/30 transition-colors flex items-center gap-1.5"
            data-testid="supplier-signout"
          >
            <SignOut size={12} weight="bold" /> Sign out
          </button>
        </div>

        {/* Attribution card — hidden on cold start. Fetched once on mount. */}
        <SupplierAttributionCard
          supplierId={supplier?.id}
          onShareFayre={() => navigate('/dashboard/supplier/fayres')}
        />

        {/* Profile card */}
        <div
          className="rounded-2xl p-5 mb-6"
          style={{
            background: 'linear-gradient(180deg, rgba(108,85,224,0.12), rgba(194,157,82,0.05))',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shrink-0"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            >
              {CATEGORY_EMOJI[supplier.category] || '🎯'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black leading-tight truncate">
                  {supplier.business_name || 'Your business'}
                </h1>
                {isPro && (
                  <span
                    className="text-[10px] font-black px-2 py-0.5 rounded-full text-white shrink-0"
                    style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                    data-testid="studio-pass-pill"
                  >
                    STUDIO PASS
                  </span>
                )}
              </div>
              <p className="text-white/50 text-sm">
                {supplier.city || supplier.postcode || 'Add postcode'} · {supplier.category || 'pick a category'}
              </p>
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs font-bold text-white/60 mb-1">
                  <span>Profile strength</span>
                  <span>{strength}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${strength}%`, background: 'linear-gradient(90deg, #B89762, #C29D52)' }}
                  />
                </div>
                {incomplete && (
                  <button
                    onClick={() => navigate('/supplier/onboard?edit=1')}
                    className="mt-2 text-xs font-bold text-[#C29D52] hover:text-[#C29D52]/85"
                    data-testid="complete-profile"
                  >
                    Complete your profile →
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => navigate('/supplier/onboard?edit=1')}
              className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center gap-1.5"
              data-testid="edit-profile"
            >
              <PencilSimple size={12} weight="bold" /> Edit profile
            </button>
            {supplier.is_live && (
              <button
                onClick={() => navigate(`/suppliers/${supplier.id}`)}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center gap-1.5"
                data-testid="view-public"
              >
                <Eye size={12} weight="bold" /> View public page
              </button>
            )}
          </div>
        </div>

        {/* Inbox entry — primary CTA with unread pill */}
        <button
          onClick={() => navigate('/messages')}
          className="w-full flex items-center justify-between rounded-2xl px-5 py-4 mb-4 bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] transition"
          data-testid="open-inbox-btn"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            >
              <ChatsCircle size={18} weight="fill" />
            </div>
            <div className="text-left">
              <p className="font-bold text-sm">Messages</p>
              <p className="text-white/40 text-xs">
                {totalUnread > 0 ? `${totalUnread} unread message${totalUnread > 1 ? 's' : ''}` : 'You\'re all caught up'}
              </p>
            </div>
          </div>
          {totalUnread > 0 && (
            <span
              className="text-xs font-black px-2.5 py-1 rounded-full bg-[#B89762]/40 text-[#0F1014]"
              data-testid="inbox-unread-badge"
            >
              {totalUnread} New
            </span>
          )}
        </button>

        {/* Performance entry — behaviour insight hook */}
        <button
          onClick={() => navigate('/dashboard/supplier/performance')}
          className="w-full flex items-center justify-between rounded-2xl px-5 py-3 mb-4 bg-white/[0.02] border border-white/5 hover:bg-white/[0.05] transition text-left"
          data-testid="open-performance-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-sm">📈</div>
            <div>
              <p className="font-bold text-xs">Performance & visibility</p>
              <p className="text-white/40 text-[11px]">See how you compare · what to improve</p>
            </div>
          </div>
          <span className="text-white/30 text-[10px] font-bold">→</span>
        </button>

        {/* Compliance Hub — store & share PAT / PLI */}
        <button
          onClick={() => navigate('/dashboard/supplier/compliance')}
          className="w-full flex items-center justify-between rounded-2xl px-5 py-3 mb-4 bg-white/[0.02] border border-white/5 hover:bg-white/[0.05] transition text-left"
          data-testid="open-compliance-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#B89762]/30/10 border border-[#B89762]/40/20 flex items-center justify-center text-[#C29D52] text-sm">🛡️</div>
            <div>
              <p className="font-bold text-xs">Supplier Compliance Hub</p>
              <p className="text-white/40 text-[11px]">Store & share PAT / PLI · win more bookings by being ready</p>
            </div>
          </div>
          <span className="text-white/30 text-[10px] font-bold">→</span>
        </button>

        {/* Wedding Fayres — real-world demand bridge */}
        <button
          onClick={() => navigate('/dashboard/supplier/fayres')}
          className="w-full flex items-center justify-between rounded-2xl px-5 py-3 mb-4 bg-white/[0.02] border border-white/5 hover:bg-white/[0.05] transition text-left"
          data-testid="open-fayres-btn"
        >
          <div className="flex items-center gap-3">
            <div className="relative w-8 h-8 rounded-lg bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300 text-sm">
              🎪
              {fayreUnread.total_new > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center px-1 ring-2 ring-[#0F1014]"
                  data-testid="dashboard-fayre-unread-dot"
                  aria-label={`${fayreUnread.total_new} new fayre message${fayreUnread.total_new === 1 ? '' : 's'}`}
                >
                  {fayreUnread.total_new > 9 ? '9+' : fayreUnread.total_new}
                </span>
              )}
            </div>
            <div>
              <p className="font-bold text-xs flex items-center gap-1.5">
                Wedding Fayres
                {fayreUnread.total_new > 0 && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-rose-500/15 border border-rose-400/40 text-rose-200"
                    data-testid="dashboard-fayre-unread-pill"
                  >
                    💬 {fayreUnread.total_new} new
                  </span>
                )}
              </p>
              <p className="text-white/40 text-[11px]">
                {fayreUnread.total_new > 0
                  ? `${fayreUnread.total_new} new enquiry${fayreUnread.total_new === 1 ? '' : 'ies'} from couples you met at a fayre`
                  : 'Join fayres near you · meet couples in real life · convert to bookings'}
              </p>
            </div>
          </div>
          <span className="text-white/30 text-[10px] font-bold">→</span>
        </button>

        {/* Chat quota pill (free tier only) */}
        {quota && !isPro && (
          <div
            className="rounded-xl px-4 py-3 mb-4 border border-white/10 bg-white/[0.03] flex items-center justify-between"
            data-testid="chat-quota-pill"
          >
            <div className="flex items-center gap-2 text-xs">
              <Lightning size={14} weight="fill" className="text-[#C29D52]" />
              <span className="font-bold">
                {quota.active_chats}/{quota.cap} active chats
              </span>
              <span className="text-white/40">
                {quota.remaining > 0
                  ? `${quota.remaining} free ${quota.remaining === 1 ? 'slot' : 'slots'} left`
                  : 'Cap reached — upgrade to keep replying'}
              </span>
            </div>
            <button
              onClick={() => navigate('/studio-pass')}
              className="text-[11px] font-bold text-[#C29D52] hover:text-[#C29D52]/85"
              data-testid="quota-upgrade-link"
            >
              Upgrade →
            </button>
          </div>
        )}

        {/* First-lead upgrade nudge — free tier, has ≥1 lead, soft and non-blocking */}
        {showFirstLeadNudge && (
          <div
            className="rounded-2xl p-4 mb-6 border border-[#B89762]/40/20 bg-gradient-to-r from-[#B89762]/10 to-[#B89762]/10"
            data-testid="first-lead-nudge"
          >
            <div className="flex items-start gap-3">
              <Lightning size={18} weight="fill" className="text-[#C29D52] mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-sm mb-0.5">You've received a new enquiry</p>
                <p className="text-white/60 text-xs mb-3">
                  Upgrade to Studio Pass to respond instantly and secure more bookings.
                </p>
                <button
                  onClick={() => navigate('/studio-pass')}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                  data-testid="first-lead-upgrade-btn"
                >
                  Upgrade
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Four-panel counter grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <SectionCard
            icon={<Envelope size={16} weight="fill" />}
            label="Leads"
            value={leads.length}
            cta={newLeadCount > 0 ? `${newLeadCount} new` : 'New enquiries land here'}
            badge={newLeadCount > 0 ? newLeadCount : null}
            testId="panel-leads"
            onClick={() => navigate('/dashboard/supplier/leads')}
          />
          <SectionCard
            icon={<ChatsCircle size={16} weight="fill" />}
            label="Chats"
            value={chatCount}
            cta="Open your inbox"
            badge={totalUnread > 0 ? totalUnread : null}
            testId="panel-chats"
            onClick={() => navigate('/messages')}
          />
          <SectionCard
            icon={<CheckCircle size={16} weight="fill" />}
            label="Bookings"
            value={bookedCount}
            cta="Confirmed work"
            testId="panel-bookings"
            onClick={() => navigate('/dashboard/supplier/bookings')}
          />
          <SectionCard
            icon={<CalendarBlank size={16} weight="fill" />}
            label="Calendar"
            value="View"
            cta={isPro ? 'Platform + external dates' : 'View-only on free'}
            testId="panel-calendar"
            onClick={() => navigate('/dashboard/supplier/calendar')}
          />
        </div>

        {/* Create External Booking ✦ — quiet inline entry for the
            off-platform workflow (phone, fayre, DM). Same surface
            the Bookings & Calendar pages render. */}
        <button
          onClick={() => setExternalModalOpen(true)}
          data-testid="dashboard-create-external-booking"
          className="w-full mb-6 py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2"
          style={{
            background: 'transparent',
            border: '1px solid rgba(194,157,82,0.35)',
            color: '#F4E9D2',
            fontFamily: 'Georgia, serif',
          }}
        >
          ✦ Create External Booking
        </button>

        {/* Recent leads list */}
        {leads.length > 0 && (
          <div className="rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden mb-6" data-testid="leads-list">
            <div className="px-4 py-3 text-xs font-black uppercase tracking-wider text-white/40 border-b border-white/5">
              Recent enquiries
            </div>
            <ul className="divide-y divide-white/5">
              {leads.slice(0, 8).map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => navigate(`/chat/${l.chat_id}`)}
                    className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.04] text-left"
                    data-testid={`lead-row-${l.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{l.customer_name}</p>
                      <p className="text-white/50 text-xs truncate">{l.first_message}</p>
                    </div>
                    <StatusPill status={l.status} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <SupplierExternalBookingModal
        open={externalModalOpen}
        onClose={() => setExternalModalOpen(false)}
        onCreated={() => {
          // Quietly refresh — a fresh booking has landed.
          refresh();
        }}
      />
    </div>
  );
};

const SectionCard = ({ icon, label, value, cta, badge, testId, onClick }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`relative text-left rounded-xl p-4 bg-white/[0.03] border border-white/10 ${onClick ? 'hover:bg-white/[0.06] transition' : 'cursor-default'}`}
    data-testid={testId}
  >
    <div className="flex items-center gap-1.5 text-white/40 text-xs font-bold uppercase tracking-wider mb-2">
      {icon} {label}
    </div>
    <p className="text-white font-black text-2xl tabular-nums">{value}</p>
    <p className="text-white/30 text-[10px] mt-1">{cta}</p>
    {badge != null && (
      <span className="absolute top-3 right-3 text-[10px] font-black px-1.5 py-0.5 rounded-full bg-[#B89762]/40 text-[#0F1014]">
        {badge}
      </span>
    )}
  </button>
);

const StatusPill = ({ status }) => {
  const map = {
    new: { label: 'New', cls: 'bg-[#B89762]/40 text-[#0F1014]' },
    responded: { label: 'Responded', cls: 'bg-white/10 text-white/70' },
    booked: { label: 'Booked', cls: 'bg-emerald-400 text-[#0F1014]' },
  };
  const s = map[status] || map.new;
  return (
    <span className={`shrink-0 text-[10px] font-black px-2 py-0.5 rounded-full ${s.cls}`}>
      {s.label}
    </span>
  );
};

export default SupplierDashboardPage;
